<template>
    <section class="o-page-bottom-spacing--large" :class="{'u-area-disabled': pageDisabled}">
        <page-header>
            <slot slot="title">Pre-order {{ PreOrder.Brand }} - {{ PreOrder.Collection }}</slot>
            <slot slot="description">
                <p>{{ $t('preorder_edit.description') }}</p>
            </slot>
        </page-header>
        <div class="o-wrapper o-wrapper--center">
            <h2 class="c-title--border u-h3">{{ $t('preorder_edit.order_details') }}</h2>
            <v-loading :data="PreOrder" custom-class="u-pos-relative"></v-loading>

            <v-message v-if="PreOrder.SalesmanNote" custom-class="o-page-row" type="info" icon="lamp" icon-width="32px" icon-height="35">{{ PreOrder.SalesmanNote }}</v-message>
            <v-message v-if="PreOrder.DeadlineExpired" custom-class="o-page-row" type="error" icon="attention" icon-width="32px" icon-height="35">
                {{ $t('preorder.deadline_expired') }}
            </v-message>
            <v-message v-if="PreOrder.IsClosed" custom-class="o-page-row" type="info" icon="attention" icon-width="32px" icon-height="35">
                {{ $t('preorder.status_closed') }}
            </v-message>

            <section :class="{'u-disabled': !PreOrder.Id, 'u-font-small o-gutter o-grid o-grid--third o-page-row': true}">
                <v-blocks>
                    <v-block color="neutral">
                        <table class="u-fullwidth table__order-details">
                            <tr>
                                <td class="u-bold">{{ $t('preorder_fiche.brand') }}</td>
                                <td>{{ PreOrder.Brand }}</td>
                            </tr>
                            <tr>
                                <td class="u-bold">{{ $t('preorder_fiche.season') }}</td>
                                <td>{{ PreOrder.Collection }}</td>
                            </tr>
                            <tr v-if="PreOrder.LastUpdate">
                                <td class="u-bold">{{ $t('preorder.last_update') }}</td>
                                <td>{{ PreOrder.LastUpdate | moment("DD-MM-YYYY") }}</td>
                            </tr>
                            <tr>
                                <td class="u-bold">{{ $t('preorder.state') }}</td>
                                <td>{{ PreOrder.State }}</td>
                            </tr>
                            <tr v-if="PreOrder.Deadline" :class="{'u-color-error-dark u-font-weight-bold': PreOrder.DeadlineExpired}">
                                <td class="u-bold">{{ $t('preorder.deadline') }}</td>
                                <td>{{ PreOrder.Deadline | moment("DD-MM-YYYY") }}</td>
                            </tr>
                        </table>
                    </v-block>
                </v-blocks>

                <v-blocks>
                    <v-block color="neutral">
                        <table class="u-fullwidth u-margin-none table__order-details">
                            <tr>
                                <td class="u-bold">{{ $t('preorder.customer_key') }}</td>
                                <td>{{ PreOrder.CustomerAltKey }}</td>
                            </tr>
                            <tr>
                                <td class="u-bold">{{ $t('preorder.customer_name') }}</td>
                                <td>{{ PreOrder.Customer }}</td>
                            </tr>
                            <tr>
                                <td class="u-bold">{{ $t('preorder.internal_ref') }}</td>
                                <td><v-input name="internal-ref" @change="savePreOrderData(false)" v-model="PreOrder.InternalCustomerReference" :maxlength=20 :disabled="PreOrder.IsClosed"></v-input></td>
                            </tr>
                            <tr>
                                <td class="u-bold">{{ $t('preorder.order_ref') }}</td>
                                <td><v-input name="internal-order-ref" @change="savePreOrderData(false)" v-model="PreOrder.OrderReference" :maxlength=60 :disabled="PreOrder.IsClosed"></v-input></td>
                            </tr>
                            <tr>
                                <td class="u-bold">{{ $t('preorder.discount') }}</td>
                                <td>
                                    <v-input name="order-max-discount" @change="savePreOrderData(true)" v-model="PreOrder.PreOrderDiscount" type="number" :min=0 :max="PreOrder.MaximumDiscount" :disabled="PreOrder.IsClosed">
                                        <slot slot="suffix"><span class="u-font-small u-text-nowrap">({{ $t('product.thumb.max') }} {{ PreOrder.MaximumDiscount }}%)</span></slot>
                                    </v-input>
                                </td>
                            </tr>
                            <tr>
                                <td class="u-bold">{{ $t('preorder.forecasts_visible') }}</td>
                                <td>
                                    <v-switch v-model="PreOrder.ForecastsAndHistoryVisible" :name="`forecast_${_uid.toString()}`" :readOnly="PreOrder.IsClosed"></v-switch>
                                </td>
                            </tr>
                            <tr>
                                <td class="u-bold">{{ $t('preorder.early_shipment_allowed') }}</td>
                                <td>
                                    <v-switch v-model="PreOrder.EarlyShipmentAllowed" :name="`early_shipment_allowed_${_uid.toString()}`" :readOnly="PreOrder.IsClosed"></v-switch>
                                </td>
                            </tr>
                            <tr>
                                <td class="row__delivery-address" colspan="2">
                                    <div class="u-bold u-margin-bottom-tiny">{{ $t('preorder.delivery_address') }}</div>
                                    <v-address-select v-if="PreOrder.CustomerKey"
                                                      v-model="PreOrder.Address"
                                                      :customerKey="PreOrder.CustomerKey"
                                                      :readOnly="PreOrder.IsClosed"
                                                      :error="Validate.Address"
                                                      @input="savePreOrderData(false)">
                                    </v-address-select>
                                </td>
                            </tr>
                        </table>
                    </v-block>
                    <v-block v-if="PreOrder.ForecastsAndHistoryVisible || PreOrder.EarlyShipmentAllowed" color="green">
                        <v-checklist>
                            <v-checklist-item v-if="PreOrder.ForecastsAndHistoryVisible">{{ $t('preorder.forecasts_and_history_visible') }}</v-checklist-item>
                            <v-checklist-item v-if="PreOrder.EarlyShipmentAllowed">{{ $t('preorder.early_shipment_allowed') }}</v-checklist-item>
                        </v-checklist>
                    </v-block>
                </v-blocks>

                <v-blocks spacing="none">
                    <v-block color="neutral">
                        <table class="u-fullwidth u-margin-none">
                            <tr>
                                <td class="u-bold" style="width:73%">{{ $t('preorder.product_count') }}</td>
                                <td>{{ PreOrderTotals.ProductCount }}</td>
                            </tr>
                            <tr>
                                <td class="u-bold" style="width:73%">{{ $t('preorder.product_variant_count') }}</td>
                                <td>{{ PreOrderTotals.ProductVariantAmount }}</td>
                            </tr>
                            <tr>
                                <td class="u-bold" style="width:73%">{{ $t('preorder.total_price') }}</td>
                                <td>{{ PreOrderTotals.TotalProductPrice | currency(customerCurrencySymbol) }}</td>
                            </tr>
                        </table>
                    </v-block>
                    <v-block color="base">
                        <table class="u-fullwidth u-margin-none">
                            <tr>
                                <td class="u-bold u-font-default" style="width:73%">{{ $t('preorder.total_price_after_discount') }}</td>
                                <td>{{ PreOrderTotals.TotalProductPriceAfterDiscount | currency(customerCurrencySymbol) }}</td>
                            </tr>
                        </table>
                    </v-block>
                    <v-block customClass="c-block-group--horizontal" size="small" color="neutral">
                        <div class="o-grid o-grid--third o-gutter-small c-preorder-alternate-data">
                            <v-sub-group-totals></v-sub-group-totals>
                            <v-targets-and-totals></v-targets-and-totals>
                            <v-delivery-moment-totals></v-delivery-moment-totals>
                        </div>
                    </v-block>
                    <v-block size="small">
                        <div class="c-button-group c-button-group--preorder">
                            <v-send-reminders :disabled="PreOrder.IsClosed"></v-send-reminders>
                            <button :disabled="PreOrder.IsClosed" @click.prevent="NotifySaved" class="c-button c-button--fullwidth c-button--outline">{{ $t('preorder.save_without_closing') }}</button>
                            <v-close-pre-order :disabled="PreOrder.IsClosed" @validateForm="validateForm"></v-close-pre-order>
                        </div>
                    </v-block>
                </v-blocks>
            </section>

            <transition name="loading">
              <v-attention v-if="PreOrderNotifications && PreOrderNotifications.length">
                  <slot slot="title">{{ $t('global.attention_required') }}</slot>
                  <slot slot="message">{{ $t('global.attention_required_description') }}</slot>

                  <v-attention-item v-for="notification in PreOrderNotifications" :item="notification" :key="notification.Id" :type="notification.Type">
                      {{ notification.Message}}
                      <slot slot="action-text">{{ $t('preorder.reported_to_customer') }}</slot>
                  </v-attention-item>

                  <slot slot="dismissAll">{{ $t('preorder.report_all_question') }}</slot>
                  <slot slot="dismissAllDone">{{ $t('preorder.all_reported') }}</slot>
              </v-attention>
            </transition>

            <section :class="{ 'o-page-row--medium': true, 'u-disabled': !filteredProducts.length }">
                <v-form-row>
                    <v-textarea v-model="PreOrder.InternalComment" :horizontal="false" name="opmerkingen" cols="20" rows="6" value="" :fluid="true" custom-label-class="u-margin-bottom-small" :readOnly="PreOrder.IsClosed">
                        <slot slot="label"><strong class="u-medium u-font-medium u-font-italic u-margin-left u-margin-right-small">{{ $t('preorder.remarks') }}</strong></slot>
                    </v-textarea>
                </v-form-row>

                <v-form-row>
                    <v-textarea v-model="PreOrder.CustomerComment" :horizontal="false" name="opmerkingen_klanten" cols="20" rows="6" value="" :fluid="true" custom-label-class="u-margin-bottom-small" :readOnly="PreOrder.IsClosed">
                        <slot slot="label"><strong class="u-medium u-font-medium u-font-italic u-margin-left u-margin-right-small">{{ $t('preorder.customer_remarks') }}</strong></slot>
                    </v-textarea>
                </v-form-row>
            </section>

            <h2 class="c-title--border u-h3">{{ $t('preorder.products') }}</h2>
            <v-loading :data="loaded" custom-class="u-pos-relative"></v-loading>
        </div>

        <v-filters :custom-class="loadingProducts ? 'u-disabled o-page-row' : 'o-page-row'">
            <v-filter-input :term.sync="searchTerm"
                            name="search-products"
                            :disabled="loadingProducts"
                            :placeholder="$t('edit_preorder.filter_placeholder')"
                            @keyup.enter.prevent="searchPreOrderProducts"
                            label="search">
            </v-filter-input>
          <label class="c-checkbox"><input class="c-checkbox__input" name="amountsOnly" id="amountsOnly" type="checkbox" v-model="amountsOnly"/>{{ $t('edit_preorder.amountsOnly') }}</label>
          <slot slot="search-button">
            <button @click.prevent="searchPreOrderProducts" :disabled="loadingProducts" class="c-button c-button--alpha c-button--small" title="Zoeken">
              <svgicon name="search"></svgicon>
              {{ $t('global.search') }}
            </button>

            <button v-if="filteredProducts.length > 0"
                    type="button"
                    class="[ c-button c-button--small c-button--white ] u-flex--push-left"
                    @click="collapseAllVariations = !collapseAllVariations">
              {{collapseAllVariations ? $t('preorder_fiche.toggle_variations.expand_all') : $t('preorder_fiche.toggle_variations.collapse_all')}}
            </button>
          </slot>
        </v-filters>

        <!-- PRODUCTEN -->
        <section id="products" :class="{ 'o-wrapper o-wrapper--center o-page-row--huge u-pos-relative': true, 'u-disabled': !filteredProducts.length || loadingProducts }">

            <v-pagination class="c-pager o-page-row"
                          :config="PaginationConfig"
                          :pagination="PaginationData"
                          @paginated="fetchPaginated"
            ></v-pagination>

            <v-product-line v-for="(group, index) in filteredProducts"
                            :parent-id="PreOrder.Id"
                            :group-index="index"
                            :key="group.Id"
                            :group="group"
                            :collapseAllVariations="collapseAllVariations"
                            :readOnly="isReadOnly"
                            :sizeChartCopy="sizeChartCopy"
                            @sizeChartCopied="sizeChartCopied">
            </v-product-line>
            <v-empty-state v-if="!PreOrderProducts.ProductGroups">
                <slot slot="title">{{ $t('edit_preorder.no_products') }}</slot>
            </v-empty-state>
            <v-empty-state v-if="PreOrderProducts.ProductGroups && !filteredProducts.length">
                <slot slot="title">{{ $t('edit_preorder.no_results') }}</slot>
            </v-empty-state>

            <v-pagination class="c-pager o-page-row"
                          :config="PaginationConfig"
                          :pagination="PaginationData"
                          @paginated="fetchPaginated"
            ></v-pagination>
        </section>

        <!-- MARKETING -->
        <section class="o-wrapper o-wrapper--center o-page-row--medium is-marketing-line">
            <h2 class="c-title--border u-h3">{{ $t('preorder.marketing_products') }}</h2>
            <v-marketing-line v-for="(group, index) in PreOrderMarketingProducts.MarketingProductGroups"
                            :parent-id="PreOrder.Id"
                            :group-index="index"
                            :key="group.Id"
                            :group="group"
                            :readOnly="isReadOnly">
            </v-marketing-line>
            <v-empty-state v-if="!PreOrderMarketingProducts.MarketingProductGroups || !PreOrderMarketingProducts.MarketingProductGroups.length">
                <slot slot="title">{{ $t('edit_preorder.no_marketingproducts') }}</slot>
            </v-empty-state>
        </section>
        <v-chatbox :receiver="PreOrder.Customer"
                   :unreadMessages="PreOrder.UnreadMessages"
                   @update-unread="updateUnread">
        </v-chatbox>
        <v-pre-order-concurreny-manager @concurrencyException="onConcurrencyException"></v-pre-order-concurreny-manager>
    </section>
</template>

<script>
    import debounce from 'lodash/debounce'
    import PageHeader from '@/components/common/PageHeader'
    import VLoading from '@/components/Loading'
    import VMessage from '@/components/Message'
    import VBlocks from '@/components/blocks/Blocks'
    import VChecklist from '@/components/checklist/Checklist'
    import VAttention from '@/components/attention/Attention'
    import VProductLine from '@/components/product/common/ProductLine'
    import VMarketingLine from '@/components/product/common/MarketingLine'
    import VEmptyState from '@/components/EmptyState'
    import VModal from '@/components/modal/Modal'
    import VAddressSelect from '@/components/form/addressSelect/AddressSelect'
    import VSubGroupTotals from '@/components/modal/common/SubGroupTotals'
    import VDeliveryMomentTotals from '@/components/modal/common/DeliveryMomentTotals'
    import VTargetsAndTotals from '@/components/modal/common/TargetsAndTotals'
    import VSendReminders from '@/components/modal/salesman/SendReminders'
    import VClosePreOrder from '@/components/modal/salesman/ClosePreOrder'
    import VChatbox from "@/components/chat/Chatbox"
    import VPreOrderConcurrenyManager from "@/components/concurrency/PreOrderConcurrencyManager"
    import VPagination from '@/components/Pagination'

    // Forms
    import VTextarea from '@/components/form/Textarea'
    import VFilters from '@/components/form/filters/Filters'
    import VFormRow from '@/components/form/Row'
    import VSelect from '@/components/form/Select'
    import VCustomSelect from '@/components/form/CustomSelect'
    import VInput from '@/components/form/Input'
    import VSwitch from '@/components/form/Switch'

    // Store
    import {mapGetters} from 'vuex'


    export default {
        components: {
            PageHeader,
            VLoading,
            VSelect,
            VMessage,
            VBlocks,
            VChecklist,
            VAttention,
            VTextarea,
            VFormRow,
            VFilters,
            VProductLine,
            VMarketingLine,
            VEmptyState,
            VModal,
            VAddressSelect,
            VSubGroupTotals,
            VDeliveryMomentTotals,
            VTargetsAndTotals,
            VSendReminders,
            VClosePreOrder,
            VCustomSelect,
            VInput,
            VSwitch,
            VChatbox,
            VPreOrderConcurrenyManager,
            VPagination
        },
        data() {
            return {
                originalDiscount: 0,
                disableWatch: true,
                loaded: {},
                loadingProducts: false,
                collapseAllVariations: false,
                Page: 1,
                searchTerm: '',
                amountsOnly: false,
                pageDisabled: false,
                Validate: {},
                sizeChartCopy: null
            };
        },
        computed: {
            ...mapGetters({
                PreOrder: 'PreOrder',
                PreOrderTotals: 'PreOrderTotals',
                PreOrderNotifications: 'PreOrderNotifications',
                PreOrderProducts: 'PreOrderProducts',
                PreOrderMarketingProducts: 'PreOrderMarketingProducts',
                CustomerAddressOptions: 'CustomerAddressOptions',
                CurrencySymbol: 'CurrencySymbol'
            }),
            filteredProducts() {
                return this.PreOrderProducts.ProductGroups ? this.PreOrderProducts.ProductGroups : [];
            },
            isReadOnly() {
                return this.PreOrder.IsClosed || this.PreOrder.DeadlineExpired;
            },
            PaginationData() {
              return {
                PageStartIndex: this.PreOrderProducts.PageStartIndex,
                PageEndIndex: this.PreOrderProducts.PageEndIndex,
                PageLastIndex: this.PreOrderProducts.PageCount,
                TotalCount: this.PreOrderProducts.TotalCount,
                Pages: this.PreOrderProducts.Pages,
                PageCount: this.PreOrderProducts.PageCount
              };
            },
            PaginationConfig() {
              return {
                Page: this.Page
              };
            },
            customerCurrencySymbol() { 
                return this.$store.getters.userInfo.customerCurrencySymbol;
            }
        },
        created() {
          this.loadingProducts = true;

          // Load preorder data
          this.$store.dispatch('getPreOrderById', this.$attrs.id).then(() => {
              this.originalDiscount = this.PreOrder.PreOrderDiscount;
              this.disableWatch = false;
              this.$store.dispatch('getCurrencySymbol', this.PreOrder.CustomerCurrency).then(() => {
                  this.$store.getters.userInfo.customerCurrencySymbol = this.CurrencySymbol;
              });
          });

          // Load preorder products with a limit
          this.$store.dispatch('getPreOrderProductsById', {
            id: this.$attrs.id,
            body: {
              PageSize: 10
            }
          }).then(() => {
            this.loadingProducts = false;
            this.loaded = { loaded: true };

            //Load these data here, so we don't create duplicate notifications
            // Load totals
            this.$store.dispatch('getPreOrderTotalsById', this.$attrs.id);
            // Load notifications
            this.$store.dispatch('getPreOrderNotificationsById', this.$attrs.id);
          });

          // Load marketing products
          this.$store.dispatch('getPreOrderMarketingProductsById', {
            id: this.$attrs.id,
            body: {
              PageSize: 400
            }
          });
        },
        watch: {
            "PreOrder.InternalComment"() {
                if (!this.disableWatch) {
                    this.savePreOrderData(false);
                }
            },
            "PreOrder.CustomerComment"() {
                if (!this.disableWatch) {
                    this.savePreOrderData(false);
                }
            },
            "PreOrder.ForecastsAndHistoryVisible"() {
                if (!this.disableWatch) {
                    this.savePreOrderData(false);
                }
            },
            "PreOrder.EarlyShipmentAllowed"() {
                if (!this.disableWatch) {
                    this.savePreOrderData(false);
                }
            }
        },
        methods: {
            validateForm(response) {
                let errors = response.data.ModelState;

                // Get error data from server, and transform to vue data
                Object.keys(errors).forEach(function(key) {
                    key = key.replace('model.', '');
                    errors[key] = errors['model.'+key][0];
                    delete errors['model.'+key];
                });

                this.Validate = errors;
            },
            savePreOrderData: debounce(function (allowFetch) {
                allowFetch = allowFetch === true;
                
                this.$store.dispatch('updatePreOrderSalesman', {
                    PreOrderId: this.PreOrder.Id,
                    body: {
                        Id: this.PreOrder.Id,
                        Address: this.PreOrder.Address,
                        InternalCustomerReference: this.PreOrder.InternalCustomerReference,
                        OrderReference: this.PreOrder.OrderReference,
                        PreOrderDiscount: this.PreOrder.PreOrderDiscount,
                        ForecastsAndHistoryVisible: this.PreOrder.ForecastsAndHistoryVisible,
                        EarlyShipmentAllowed: this.PreOrder.EarlyShipmentAllowed,
                        InternalComment: this.PreOrder.InternalComment,
                        CustomerComment: this.PreOrder.CustomerComment
                    }
                }).then(() => {
                    if (allowFetch) {

                        this.fetchPreOrder();

                        if (this.originalDiscount != this.PreOrder.PreOrderDiscount) {
                            this.originalDiscount = this.PreOrder.PreOrderDiscount;
                            this.fetchPreOrderProducts();
                        }
                    }
                }).catch((e) => {
                    this.NotifyError(e.ExceptionMessage);
                    this.fetchPreOrder();
                });
            }, 500),
            fetchPreOrder() {
                this.$store.dispatch('getPreOrderById', this.$attrs.id);
            },
            fetchPreOrderProducts() {
              this.loadingProducts = true;
              this.loaded = {};

              this.$store.dispatch('getPreOrderProductsById', {
                id: this.$attrs.id,
                body: {
                  Page: this.Page,
                  PageSize: 10,
                  Search: this.searchTerm,
                  AmountsOnly: this.amountsOnly
                }
              }).then(() => {
                this.loadingProducts = false;
                this.loaded = { loaded: true };
              }).catch(() => {
                this.loadingProducts = false;
                this.loaded = { loaded: true };
              });
            },
            searchPreOrderProducts() {
              this.Page = 1;
              this.fetchPreOrderProducts();
            },
            fetchPaginated(config) {
                this.scrollToProductTop();

                this.Page = config.Page;
                this.fetchPreOrderProducts();
            },
            scrollToProductTop() {
                var options = {
                    container: 'body',
                    easing: 'ease-in',
                    offset: -60,
                    cancelable: true,
                    onStart: function(element) { },
                    onDone: function(element) { },
                    onCancel: function() { },
                    x: false,
                    y: true
                };
                var cancelScroll = this.$scrollTo("#products", 500, options);
            },
            NotifySaved() {
                this.$notify({
                    group: 'global',
                    title: this.$t('global.saved'),
                    type: 'success',
                    duration: 2000
                });
            },
            NotifyError(message) {
                this.$notify({
                    group: 'global',
                    title: message,
                    type: 'error',
                    duration: 5000
                });
            },
            onConcurrencyException() {
                this.pageDisabled = true;
                this.$notify({
                    group: 'global',
                    title: this.$t('concurrency.pre_order'),
                    type: 'info',
                    duration: 5000
                });
                setTimeout(function() {
                    location.reload(true);
                }, 4000);
            },
            updateUnread(value) {
                this.$set(this.PreOrder, 'UnreadMessages', value);
            },
            sizeChartCopied(sizeChartCopy) {
                this.sizeChartCopy = sizeChartCopy;
            }
        },
        beforeDestroy() {
            this.$store.dispatch('resetPreOrder');
        }
    }
</script>

<style lang="scss" scoped>
    @import "~@/assets/scss/private";

    .row__delivery-address {
        vertical-align: top;
        max-width: 50%;
    }
    @include down($tablet) {
        table.u-fullwidth {
            td:first-child {
                width: 50%;
            }
        }
    }
</style>



// WEBPACK FOOTER //
// src/views/salesman/preOrders/Edit.vue